import { useOwnership } from '@commenda/api';
import { useCallback } from 'react';
import {
  DEFAULT_PERCENT_SCALE,
  DEFAULT_UBO_THRESHOLD_PERCENT,
  STAGED_STOCK_CLASS_ID,
} from '../constants';
import { ZERO_BUDGET, type GetOwnershipBudget } from '../schemas';

interface DerivationArgs {
  /** Shareholding being edited (its row is excluded from budget sums). */
  excludeStakeholdingId?: string | null;
}

// Budget is per-class: shareholdings on the same class (or the null bucket)
// share one 100% pool. Returns a `getBudget(stockClassId)` helper the schema
// uses at validation time so it always sees the bucket the form is writing to.
export function useOwnershipFormDerivations(
  corporationId: number,
  args?: DerivationArgs,
) {
  const excludeStakeholdingId = args?.excludeStakeholdingId ?? null;
  const { data: ownership } = useOwnership(corporationId);
  const uboThreshold =
    ownership?.entity.uboThresholdPercent ?? DEFAULT_UBO_THRESHOLD_PERCENT;
  const percentScale = ownership?.percentScale ?? DEFAULT_PERCENT_SCALE;
  const shareholdings = ownership?.shareholdings ?? [];

  const getBudget = useCallback<GetOwnershipBudget>(
    (stockClassId) => {
      // A staged class doesn't exist yet, so nothing is issued against it and the
      // whole 100% is free. Deliberately not the null bucket — that one is a real
      // pool holding the unassigned legacy rows.
      if (stockClassId === STAGED_STOCK_CLASS_ID) return ZERO_BUDGET;

      const inBucket = shareholdings.filter(
        (s) => (s.stockClass?.id ?? null) === (stockClassId ?? null),
      );
      const bucketSumScaled = inBucket.reduce(
        (acc, s) => acc + (s.ownershipPercent ?? 0),
        0,
      );
      // The row being edited counts toward `oldOwn` only if it lived in the
      // bucket the form is writing to — moving across classes frees the old
      // bucket's slot entirely.
      const editing = excludeStakeholdingId
        ? shareholdings.find((s) => s.stakeholdingId === excludeStakeholdingId) ?? null
        : null;
      const inSameBucket =
        !!editing &&
        (editing.stockClass?.id ?? null) === (stockClassId ?? null);
      const oldOwnScaled = inSameBucket ? editing!.ownershipPercent ?? 0 : 0;
      return {
        otherSumPercent: Math.max(
          0,
          (bucketSumScaled - oldOwnScaled) / percentScale,
        ),
        oldOwnPercent: oldOwnScaled / percentScale,
      };
    },
    [shareholdings, percentScale, excludeStakeholdingId],
  );

  return {
    uboThreshold,
    percentScale,
    percentStep: 1 / percentScale,
    getBudget,
  };
}
